import { Button } from "@msuite/ui/button";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon, icon } from "@msuite/ui/icons";
import { LazyFallback } from "@msuite/ui/loading-screen";
import { useTranslation } from "@msuite/ui/localization";
import { Menu, withoutDanglingDividers } from "@msuite/ui/menu";
import { ConfirmModal, useAsyncModal } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { RowActionsWrapper, useDataTableContext } from "@msuite/ui/table";
import type { ProjectInvoice, ProjectInvoiceWithProject } from "@msuite/utils/types";
import React, { Suspense } from "react";
import { useCitadel } from "@/hooks/use-citadel";
import { useHiddenProjectTabs } from "@/hooks/use-project-tabs";
import { client } from "@/hono";
import { projectTabPath } from "../../tabs";

const EditProjectInvoiceModal = React.lazy(
	() => import("@/modals/projects/edit-project-invoice-modal"),
);

/** Props Interface */
interface RowActionsProps {
	invoice: ProjectInvoiceWithProject | ProjectInvoice;
	withLinkToProject?: boolean;
}

export function RowActions({ invoice, withLinkToProject }: RowActionsProps) {
	const { refetch } = useDataTableContext();

	/** Hooks */
	const editProjectInvoiceModal = useDisclosure();
	const { t } = useTranslation();
	const asyncModal = useAsyncModal();
	const qc = useQueryClient();
	const { citadel } = useCitadel();
	const hiddenTabs = useHiddenProjectTabs();

	/** Functions */
	async function handleOnDelete() {
		try {
			const isConfirmed = await asyncModal.confirm();
			if (!isConfirmed) return;
			await client.projects[":projectId"].invoices[":invoiceId"].$delete({
				param: {
					projectId: invoice.projectId,
					invoiceId: invoice.id,
				},
			});
			await qc.invalidateQueries({
				predicate: invalidate(["invoices", invoice.projectId]),
			});
		} catch (error) {
			console.error(error);
		}
	}

	/** Computed */
	// The rules bind against the invoice itself, because `Invoice` reaches either the whole
	// organisation or what the caller booked, and the row is here to be asked about. Same
	// three-state guard as `Can` documents: an entry disappears on a definitive denial only, so
	// rules still loading keep it and let the server have the last word.
	const editIsDenied = !!citadel && citadel.cannot("update", "Invoice", { ...invoice });
	const deleteIsDenied = !!citadel && citadel.cannot("delete", "Invoice", { ...invoice });
	// Each entry is decided on its own, so the separators between them are written unconditionally
	// and the ones left with nothing on one side are dropped. What survives also answers whether
	// the menu is worth opening: on the project's own tab the link to the project is not offered,
	// so a caller denied both writes has nothing to open.
	const menuItems = withoutDanglingDividers([
		withLinkToProject
			? {
					label: t("projects.project"),
					onClick: () => {
						window.open(`/projects/${invoice.projectId}`);
					},
					icon: icon("ExternalLink"),
				}
			: null,
		"divider",
		editIsDenied
			? null
			: {
					label: t("invoices.edit_invoice"),
					onClick: editProjectInvoiceModal.onOpen,
					icon: icon("Edit"),
				},
		"divider",
		deleteIsDenied
			? null
			: {
					label: t("general.delete"),
					onClick: handleOnDelete,
					icon: icon("Trash2"),
					isDanger: true,
				},
	]);

	/** Render */
	return (
		<RowActionsWrapper to={projectTabPath(invoice.projectId, "invoices", hiddenTabs)}>
			{menuItems.length > 0 && (
				<Menu items={() => menuItems}>
					<Button
						size="xxs"
						icon={<Icon name="EllipsisVertical" />}
						variant="ghost"
					/>
				</Menu>
			)}
			{editProjectInvoiceModal.isOpen && (
				<Suspense fallback={<LazyFallback variant="portal" />}>
					<EditProjectInvoiceModal
						isOpen={editProjectInvoiceModal.isOpen}
						onClose={editProjectInvoiceModal.onClose}
						invoiceId={invoice.id}
						projectId={invoice.projectId}
						onComplete={async () => {
							await refetch?.();
						}}
					/>
				</Suspense>
			)}
			{asyncModal.isOpen && (
				<ConfirmModal
					controller={asyncModal}
					message={t("invoices.delete_invoice_cofirm_message")}
					confirmLabel={t("general.delete")}
				/>
			)}
		</RowActionsWrapper>
	);
}
